import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import buyingGuideService from '../../../services/buyingGuideService';

const GuideFormModal = ({ guide, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: 'home-size',
    targetAudience: '',
    summary: '',
    content: '',
    seoTitle: '',
    seoDescription: '',
    readTimeMinutes: 10,
    featured: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (guide) {
      setFormData({
        title: guide?.title || '',
        category: guide?.category || 'home-size',
        targetAudience: guide?.targetAudience || '',
        summary: guide?.summary || '',
        content: guide?.content || '',
        seoTitle: guide?.seoTitle || '',
        seoDescription: guide?.seoDescription || '',
        readTimeMinutes: guide?.readTimeMinutes || 10,
        featured: guide?.featured || false
      });
    }
  }, [guide]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      if (guide?.id) {
        await buyingGuideService?.updateGuide(guide?.id, formData);
      } else {
        await buyingGuideService?.createGuide(formData);
      }

      onSave();
    } catch (err) {
      console.error('Error saving guide:', err);
      setError(err?.message || 'Failed to save guide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-bold text-text-primary">
            {guide ? 'Edit Buying Guide' : 'Create Buying Guide'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/50 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Title *
              </label>
              <Input
                name="title"
                value={formData?.title}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Category *
                </label>
                <Select
                  name="category"
                  value={formData?.category}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                >
                  <option value="home-size">Home Size</option>
                  <option value="floor-type">Floor Type</option>
                  <option value="lifestyle">Lifestyle</option>
                  <option value="budget">Budget</option>
                  <option value="seasonal">Seasonal</option>
                  <option value="technology">Technology</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Read Time (minutes)
                </label>
                <Input
                  type="number"
                  name="readTimeMinutes"
                  value={formData?.readTimeMinutes}
                  onChange={handleInputChange}
                  min="1"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Target Audience
              </label>
              <Input
                name="targetAudience"
                value={formData?.targetAudience}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Summary
              </label>
              <textarea
                name="summary"
                value={formData?.summary}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                rows={3}
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Content *
              </label>
              <textarea
                name="content"
                value={formData?.content}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                rows={10}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                SEO Title
              </label>
              <Input
                name="seoTitle"
                value={formData?.seoTitle}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                SEO Description
              </label>
              <textarea
                name="seoDescription"
                value={formData?.seoDescription}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                rows={3}
                disabled={loading}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={formData?.featured}
                onChange={handleInputChange}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                disabled={loading}
              />
              <label htmlFor="featured" className="text-sm font-medium text-text-primary">
                Featured Guide
              </label>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            iconName="Save"
            iconPosition="left"
          >
            {loading ? 'Saving...' : 'Save Guide'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GuideFormModal;